/**
 * Trends Insights Summary
 * AI-generated narrative summary of wellness trends with typewriter effect
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Award, Target } from 'lucide-react';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

interface InsightData {
  type: 'positive' | 'concern' | 'neutral';
  icon: React.ReactNode;
  title: string;
  message: string;
}

export default function TrendsInsightsSummary() {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['trends-insights'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const startDate = format(subDays(new Date(), 14), 'yyyy-MM-dd');
      
      const { data: checkins } = await supabase
        .from('daily_checkins')
        .select('date, hours_slept, sleep_quality, body_feeling, daily_feeling, morning_completed, evening_completed')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (!checkins || checkins.length < 3) return null;

      // Analyse patterns
      const feelingToScore = (feeling: string | null): number => {
        if (!feeling) return 5;
        const map: Record<string, number> = {
          'amazing': 10, 'good': 8, 'okay': 6, 'struggling': 4, 'crisis': 2,
          'Energized & Vibrant': 10, 'Strong & Comfortable': 8, 'Neutral': 6,
          'Tired or Low Energy': 4, 'In Pain or Discomfort': 2
        };
        return map[feeling] ?? 5;
      };

      const moodScores = checkins.map(c => feelingToScore(c.daily_feeling));
      const energyScores = checkins.map(c => feelingToScore(c.body_feeling));
      const sleepScores = checkins.map(c => c.sleep_quality || 5);

      const avgMood = moodScores.reduce((a, b) => a + b, 0) / moodScores.length;
      const avgEnergy = energyScores.reduce((a, b) => a + b, 0) / energyScores.length;
      const avgSleep = sleepScores.reduce((a, b) => a + b, 0) / sleepScores.length;

      // Check-in consistency
      const checkInRate = checkins.length / 14;
      const completedBoth = checkins.filter(c => c.morning_completed && c.evening_completed).length;

      // Calculate trends (first half vs second half)
      const halfPoint = Math.floor(checkins.length / 2);
      const recentMood = moodScores.slice(0, halfPoint).reduce((a, b) => a + b, 0) / halfPoint;
      const olderMood = moodScores.slice(halfPoint).reduce((a, b) => a + b, 0) / (checkins.length - halfPoint);
      const moodTrend = ((recentMood - olderMood) / olderMood) * 100;

      return {
        totalCheckIns: checkins.length,
        checkInRate,
        completedBoth,
        avgMood,
        avgEnergy,
        avgSleep,
        moodTrend,
        bestDay: checkins.reduce((best, c) => 
          feelingToScore(c.daily_feeling) > feelingToScore(best.daily_feeling) ? c : best
        ),
      };
    }
  });

  const generatedInsights = useMemo<InsightData[]>(() => {
    if (!insights) return [];
    
    const result: InsightData[] = [];

    // Check-in consistency insight
    if (insights.checkInRate >= 0.7) {
      result.push({
        type: 'positive',
        icon: <Award className="w-5 h-5 text-primary" />,
        title: 'Consistent Self-Awareness',
        message: `You've checked in ${insights.totalCheckIns} times in the last 2 weeks. This consistency builds the foundation for meaningful pattern recognition.`
      });
    } else if (insights.checkInRate < 0.3) {
      result.push({
        type: 'concern',
        icon: <Target className="w-5 h-5 text-amber-500" />,
        title: 'Check-In Opportunity',
        message: 'More frequent check-ins would help us understand your patterns better. Even quick check-ins make a difference.'
      });
    }

    // Mood trend insight
    if (insights.moodTrend > 10) {
      result.push({
        type: 'positive',
        icon: <TrendingUp className="w-5 h-5 text-primary" />,
        title: 'Upward Momentum',
        message: `Your mood has improved ${Math.abs(insights.moodTrend).toFixed(0)}% recently. Something is working - keep noticing what supports you.`
      });
    } else if (insights.moodTrend < -10) {
      result.push({
        type: 'concern',
        icon: <TrendingDown className="w-5 h-5 text-destructive" />,
        title: 'Gentle Attention Needed',
        message: `Your mood has dipped ${Math.abs(insights.moodTrend).toFixed(0)}% recently. This isn't a failure - it's information. What has changed?`
      });
    }

    // Sleep-energy connection
    if (insights.avgSleep < 5 && insights.avgEnergy < 6) {
      result.push({
        type: 'concern',
        icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
        title: 'Sleep-Energy Pattern',
        message: 'Both sleep quality and energy are running low. Your body may be asking for more rest and restoration.'
      });
    } else if (insights.avgSleep > 7 && insights.avgEnergy > 7) {
      result.push({
        type: 'positive',
        icon: <Sparkles className="w-5 h-5 text-primary" />,
        title: 'Restorative Balance',
        message: 'Your sleep and energy are both strong. This foundation supports everything else in your life.'
      });
    }

    return result;
  }, [insights]);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="w-5 h-5 text-primary" />
          </motion.div>
          <span className="text-muted-foreground">Analysing your patterns...</span>
        </div>
      </Card>
    );
  }

  if (!insights || generatedInsights.length === 0) {
    return (
      <Card className="p-6 text-center">
        <Sparkles className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          Complete more check-ins to unlock personalised insights
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <div className="p-2 rounded-lg bg-accent/10">
          <Sparkles className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">AI Insights</h3>
          <p className="text-xs text-muted-foreground">Based on {insights.totalCheckIns} check-ins</p>
        </div>
      </motion.div>

      {/* Insights Cards */}
      <div className="space-y-3">
        {generatedInsights.map((insight, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.15 }}
          >
            <Card className={`p-4 ${
              insight.type === 'positive' ? 'bg-primary/5 border-primary/20' :
              insight.type === 'concern' ? 'bg-amber-500/5 border-amber-500/20' :
              'bg-muted/30'
            }`}>
              <div className="flex items-start gap-3">
                <div className="mt-0.5">{insight.icon}</div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{insight.title}</span>
                    <Badge variant={insight.type === 'positive' ? 'default' : 'secondary'} className="text-xs">
                      {insight.type === 'positive' ? 'Strength' : insight.type === 'concern' ? 'Attention' : 'Note'}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground min-h-[2.5rem]">
                    <TypewriterMessage
                      text={insight.message}
                      speed={15}
                      delay={idx * 500}
                      showCursor={false}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
